/**
 * Item Lock Store
 * 
 * Manages collaborative edit locks with Supabase Realtime subscriptions.
 * Provides real-time lock status updates across all users in a game.
 */

import { supabase } from '@/lib/config/supabase';
import { apiService, ItemLock } from '@/lib/api-functions/api-service';
import { RealtimeChannel } from '@supabase/supabase-js';

// Lock status type for individual items
export interface LockStatus {
  isLocked: boolean;
  lockedBy: string | null;
  lockedByName: string | null;
  lockedByAvatar: string | null;
  lockedAt: string | null;
  expiresAt: string | null;
  isOwnLock: boolean;
}

// Callback type for lock change notifications
type LockChangeCallback = (locks: ItemLock[]) => void;

class ItemLockStore {
  private locks: Map<string, ItemLock> = new Map();
  private subscribers: Set<LockChangeCallback> = new Set();
  private realtimeChannel: RealtimeChannel | null = null;
  private currentGameId: string | null = null;
  private currentUserId: string | null = null;

  /**
   * Initialize the store with game and user context
   */
  async initialize(gameId: string, userId: string): Promise<void> {
    // If already initialized for this game, skip
    if (this.currentGameId === gameId && this.realtimeChannel) {
      return;
    }

    // Clean up existing subscription
    await this.cleanup();

    this.currentGameId = gameId;
    this.currentUserId = userId;

    // Fetch initial locks
    await this.fetchLocks();

    // Set up realtime subscription
    this.setupRealtimeSubscription();
  }

  /**
   * Fetch all locks for the current game
   */
  private async fetchLocks(): Promise<void> {
    if (!this.currentGameId) return;

    try {
      const result = await apiService.getGameLocks(this.currentGameId);
      if (result.success && Array.isArray(result.data)) {
        this.locks.clear();
        result.data.forEach((lock: ItemLock) => {
          const key = this.getLockKey(lock.item_id, lock.item_type);
          this.locks.set(key, lock);
        });
        this.notifySubscribers();
      }
    } catch (error) {
      console.error('[ItemLockStore] Failed to fetch locks:', error);
    }
  }

  /**
   * Set up Supabase Realtime subscription for lock changes
   */
  private setupRealtimeSubscription(): void {
    if (!this.currentGameId) return;

    // Create channel for item_locks table
    this.realtimeChannel = supabase
      .channel(`item_locks:${this.currentGameId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'item_locks',
          filter: `game_id=eq.${this.currentGameId}`,
        },
        (payload) => {
          this.handleRealtimeEvent(payload);
        }
      )
      .subscribe((status) => {
      });
  }

  /**
   * Handle realtime events from Supabase
   */
  private handleRealtimeEvent(payload: any): void {
    const { eventType, new: newRecord, old: oldRecord } = payload;


    switch (eventType) {
      case 'INSERT':
      case 'UPDATE':
        if (newRecord) {
          const lock = newRecord as ItemLock;
          const key = this.getLockKey(lock.item_id, lock.item_type);
          this.locks.set(key, lock);
          this.notifySubscribers();
        }
        break;
      case 'DELETE':
        if (oldRecord) {
          const key = this.getLockKey(oldRecord.item_id, oldRecord.item_type);
          this.locks.delete(key);
          this.notifySubscribers();
        }
        break;
    }
  }

  /**
   * Get lock key for map storage
   */
  private getLockKey(itemId: string, itemType: string): string {
    return `${itemType}:${itemId}`;
  }

  /**
   * Notify all subscribers of lock changes
   */
  private notifySubscribers(): void {
    const locksArray = Array.from(this.locks.values());
    this.subscribers.forEach((callback) => {
      try {
        callback(locksArray);
      } catch (error) {
        console.error('[ItemLockStore] Subscriber error:', error);
      }
    });
  }

  /**
   * Subscribe to lock changes
   */
  subscribe(callback: LockChangeCallback): () => void {
    this.subscribers.add(callback);
    // Immediately call with current data
    callback(Array.from(this.locks.values()));
    
    return () => {
      this.subscribers.delete(callback);
    };
  }

  /**
   * Get lock status for a specific item
   */
  getLockStatus(itemId: string, itemType: string): LockStatus {
    const key = this.getLockKey(itemId, itemType);
    const lock = this.locks.get(key);

    if (!lock) {
      return {
        isLocked: false,
        lockedBy: null,
        lockedByName: null,
        lockedByAvatar: null,
        lockedAt: null,
        expiresAt: null,
        isOwnLock: false,
      };
    }

    // Check if lock is expired
    if (new Date(lock.expires_at) < new Date()) {
      this.locks.delete(key);
      return {
        isLocked: false,
        lockedBy: null,
        lockedByName: null,
        lockedByAvatar: null,
        lockedAt: null,
        expiresAt: null,
        isOwnLock: false,
      };
    }

    const profile = lock.profiles;
    return {
      isLocked: true,
      lockedBy: lock.locked_by,
      lockedByName: profile?.full_name || profile?.username || profile?.email || 'Unknown User',
      lockedByAvatar: profile?.avatar_url || null,
      lockedAt: lock.locked_at,
      expiresAt: lock.expires_at,
      isOwnLock: lock.locked_by === this.currentUserId,
    };
  }

  /**
   * Check if an item is locked by another user
   */
  isLockedByOther(itemId: string, itemType: string): boolean {
    const status = this.getLockStatus(itemId, itemType);
    return status.isLocked && !status.isOwnLock;
  }

  /**
   * Check if an item is locked by current user
   */
  isLockedByMe(itemId: string, itemType: string): boolean {
    const status = this.getLockStatus(itemId, itemType);
    return status.isLocked && status.isOwnLock;
  }

  /**
   * Get all locks
   */
  getAllLocks(): ItemLock[] {
    return Array.from(this.locks.values());
  }

  /**
   * Acquire a lock on an item
   */
  async acquireLock(
    itemId: string,
    itemType: 'object' | 'dialog' | 'document' | 'template' | 'flow',
    gameId: string
  ): Promise<{ success: boolean; lock?: ItemLock; error?: string }> {
    try {
      const result = await apiService.acquireLock(itemId, itemType, gameId);
      
      if (result.success && result.data) {
        const key = this.getLockKey(itemId, itemType);
        this.locks.set(key, result.data);
        this.notifySubscribers();
        return { success: true, lock: result.data };
      }
      
      // If failed but we got lock data back (409 conflict), cache the existing lock
      // The API returns { isLocked: true, lock: {...} } on 409 conflict
      if (!result.success && result.data?.lock) {
        const existingLock = result.data.lock as ItemLock;
        const key = this.getLockKey(itemId, itemType);
        this.locks.set(key, existingLock);
        this.notifySubscribers();
        return { success: false, error: result.error || 'Item is locked by another user', lock: existingLock };
      }
      
      return { success: false, error: result.error || 'Failed to acquire lock' };
    } catch (error) {
      console.error('[ItemLockStore] Failed to acquire lock:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Force acquire a lock on an item (kick another user)
   */
  async forceLock(
    itemId: string,
    itemType: 'object' | 'dialog' | 'document' | 'template' | 'flow',
    gameId: string
  ): Promise<{ success: boolean; lock?: ItemLock; kickedUser?: string; error?: string }> {
    try {
      const result = await apiService.forceLock(itemId, itemType, gameId);
      
      if (result.success && result.data) {
        const key = this.getLockKey(itemId, itemType);
        this.locks.set(key, result.data);
        this.notifySubscribers();
        return { success: true, lock: result.data };
      }
      
      return { success: false, error: result.error || 'Failed to force acquire lock' };
    } catch (error) {
      console.error('[ItemLockStore] Failed to force acquire lock:', error);
      return { success: false, error: error instanceof Error ? error.message : 'Unknown error' };
    }
  }

  /**
   * Refresh a lock (heartbeat)
   */
  async refreshLock(
    itemId: string,
    itemType: 'object' | 'dialog' | 'document' | 'template' | 'flow'
  ): Promise<boolean> {
    try {
      const result = await apiService.refreshLock(itemId, itemType);
      
      if (result.success && result.data) {
        const key = this.getLockKey(itemId, itemType);
        this.locks.set(key, result.data);
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('[ItemLockStore] Failed to refresh lock:', error);
      return false;
    }
  }

  /**
   * Release a lock on an item
   */
  async releaseLock(
    itemId: string,
    itemType: 'object' | 'dialog' | 'document' | 'template' | 'flow'
  ): Promise<boolean> {
    try {
      const result = await apiService.releaseLock(itemId, itemType);
      
      if (result.success) {
        const key = this.getLockKey(itemId, itemType);
        this.locks.delete(key);
        this.notifySubscribers();
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('[ItemLockStore] Failed to release lock:', error);
      return false;
    }
  }

  /**
   * Cleanup subscriptions and state
   */
  async cleanup(): Promise<void> {
    if (this.realtimeChannel) {
      await supabase.removeChannel(this.realtimeChannel);
      this.realtimeChannel = null;
    }
    this.locks.clear();
    this.currentGameId = null;
    this.subscribers.clear();
  }

  /**
   * Force refresh locks from server
   */
  async refresh(): Promise<void> {
    await this.fetchLocks();
  }
}

// Singleton instance
export const itemLockStore = new ItemLockStore();
export default itemLockStore;

